import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, ArrowRight, Check, Zap, Globe2, Lock, Database, Link as LinkIcon,
  Users2, FileCheck2, ChevronRight, Loader2, TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WATCHLISTS = [
  { name: 'OFAC SDN', desc: 'US Treasury sanctions', refresh: 'Daily' },
  { name: 'UNSCR', desc: 'UN Security Council', refresh: 'Daily' },
  { name: 'EU Consolidated', desc: 'EU financial sanctions', refresh: 'Daily' },
  { name: 'MY MOHA', desc: 'Malaysia terrorism list', refresh: 'Daily' },
  { name: 'OpenSanctions PEP', desc: 'Global PEPs', refresh: 'Weekly' },
];

const FEATURES = [
  { icon: Zap, title: 'Real-time screening', body: 'Fuzzy-match names, DOB and ID in under 2 seconds. Handles transliteration, aliases and business-entity suffixes.' },
  { icon: LinkIcon, title: 'Zero-integration deep links', body: 'Plug into MoneyPro or any legacy counter software with a URL — no API work required.' },
  { icon: Lock, title: 'Multi-tenant by design', body: 'Every branch, teller and manager isolated by organization with role-based access control.' },
  { icon: FileCheck2, title: 'Audit-ready reporting', body: 'Every scan logged with user, time and matches. One-click CSV export for BNM inspection.' },
  { icon: Database, title: 'Live data freshness', body: 'Green / yellow / red indicators on every list so you always know your data currency.' },
  { icon: Globe2, title: 'REST API for core systems', body: 'Integrate directly into your booking, KYC or transaction flow with a single API key.' },
];

const PRICING = [
  { tier: 'Starter', price: 'RM 199', period: '/ month', best: 'Single outlet', scans: '1,000 scans', users: '3 users', features: ['All 5 watchlists', 'Deep-link ready', 'CSV export', 'Email support'], highlight: false },
  { tier: 'Pro', price: 'RM 499', period: '/ month', best: 'Multi-branch MSB', scans: '5,000 scans', users: '15 users', features: ['Everything in Starter', 'Full RBAC', 'API access', 'Priority support'], highlight: true },
  { tier: 'Enterprise', price: 'Contact us', period: '', best: 'Groups, remittance, DNFBPs', scans: 'Unlimited scans', users: 'Unlimited users', features: ['Everything in Pro', 'Dedicated API keys', 'Custom watchlists', 'SLA + on-prem option'], highlight: false },
];

// Ease-out cubic counter animation, ~1.4s duration.
function AnimatedCounter({ value, className }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (typeof value !== 'number' || value <= 0) { setDisplay(value || 0); return; }
    const duration = 1400;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const progress = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * value));
      if (progress < 1) raf = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className} data-testid="animated-counter">{display.toLocaleString()}</span>;
}

export default function LandingPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '', message: '' });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/marketing/stats`, {
          withCredentials: false,
          transformRequest: [(d, headers) => { delete headers.Authorization; return d; }],
        });
        if (!cancelled) setStats(data);
      } catch (err) {
        // Ticker is hidden on failure — but log for observability so we notice
        // if the public marketing/stats endpoint starts erroring at scale.
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[LandingPage] marketing/stats fetch failed:', err?.message || err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submitDemo = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error('Please fill in name, email and company');
      return;
    }
    setSubmitting(true);
    try {
      // Public endpoint — do not send credentials/token
      const { data } = await axios.post(`${API}/marketing/demo-request`, form, {
        withCredentials: false,
        transformRequest: [(d, headers) => { delete headers.Authorization; return JSON.stringify(d); }],
        headers: { 'Content-Type': 'application/json' },
      });
      toast.success(data?.message || "Thanks! We'll be in touch soon.");
      setDemoOpen(false);
      setForm({ name: '', email: '', company: '', role: '', message: '' });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof detail === 'string' ? detail : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 antialiased" data-testid="landing-page">
      {/* NAV */}
      <nav className="border-b border-slate-800/60 backdrop-blur-md bg-slate-950/70 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-slate-950" />
            </div>
            <span className="font-semibold tracking-tight text-lg">My Shield</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-300">
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <a href="#compliance" className="hover:text-white">Compliance</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-slate-300 hover:text-white" data-testid="nav-login-link">Log in</Link>
            <Button
              size="sm"
              onClick={() => setDemoOpen(true)}
              className="bg-white text-slate-950 hover:bg-slate-200 font-semibold"
              data-testid="nav-request-demo-btn"
            >
              Request demo
            </Button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-800/60">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(56,189,248,0.15),_transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 relative">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 text-xs font-medium tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Built in Malaysia · BNM-aligned
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Screen every customer<br />
              <span className="bg-gradient-to-r from-sky-300 via-indigo-300 to-fuchsia-300 bg-clip-text text-transparent">in under 2 seconds.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Sanctions and PEP screening built for Malaysian money changers, remittance operators and DNFBPs.
              Five global watchlists, one search bar, full audit trail.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() => setDemoOpen(true)}
                className="bg-white text-slate-950 hover:bg-slate-200 font-semibold px-8 py-6 text-base"
                data-testid="hero-request-demo-btn"
              >
                Request a demo <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-100 px-8 py-6 text-base" data-testid="hero-login-btn">
                  Log in
                </Button>
              </Link>
            </div>
            <p className="mt-5 text-xs text-slate-500">14-day free trial · No card required · Cancel anytime</p>
          </div>

          {/* Watchlists strip */}
          <div className="mt-16 lg:mt-24 grid grid-cols-2 md:grid-cols-5 gap-3">
            {WATCHLISTS.map((w) => (
              <div key={w.name} className="rounded-lg border border-slate-800/70 bg-slate-900/40 p-4 hover:border-slate-700 transition-colors" data-testid={`watchlist-${w.name.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{w.refresh}</div>
                <div className="mt-2 text-sm font-semibold text-white">{w.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">{w.desc}</div>
              </div>
            ))}
          </div>

          {/* Live sanctions ticker */}
          {stats && (
            <div className="mt-10" data-testid="sanctions-ticker">
              <div className="rounded-xl border border-slate-800/70 bg-gradient-to-r from-slate-900/60 via-slate-900/40 to-slate-900/60 p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-lg bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-300" />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <AnimatedCounter value={stats.added_this_week} className="text-3xl md:text-4xl font-bold text-white tabular-nums" />
                      <span className="text-sm text-slate-400">entities added this week</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Live across {stats.sources_count} watchlists · Total indexed:
                      <span className="text-slate-300 font-medium tabular-nums">{(stats.total_entities || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <div className="hidden lg:flex items-center gap-6 border-l border-slate-800 pl-6">
                  {(stats.sources || []).slice(0, 5).map((s) => (
                    <div key={s.source} className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{s.source.replace(' Sanctions', '').replace(' SDN', '').replace('OpenSanctions ', '')}</div>
                      <div className="text-sm font-semibold text-slate-200 tabular-nums">{(s.count || 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">The problem</div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
                Manual screening is slow, error-prone, and impossible to audit.
              </h2>
            </div>
            <div className="space-y-4 text-slate-300 leading-relaxed">
              <p>Under BNM&apos;s AML/CFT Policy Document, every money changer and remittance operator must screen customers against OFAC, UN, EU, MOHA, and PEP databases.</p>
              <p>Missing a single match can cost you your license — or worse. Legacy tools cost RM 3-5k/month. Excel-based screening won&apos;t survive a BNM inspection.</p>
              <p className="text-sky-300 font-semibold">My Shield fixes this.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">What you get</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Everything you need to be audit-ready.</h2>
          </div>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-xl border border-slate-800/70 bg-slate-900/40 p-6 hover:border-slate-700 hover:bg-slate-900/60 transition-all" data-testid={`feature-${f.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500/20 to-indigo-500/20 border border-sky-400/20 flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-sky-300" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Pricing</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Simple, transparent. Local RM billing.</h2>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {PRICING.map((p) => (
              <div key={p.tier} className={`rounded-2xl border p-8 flex flex-col ${p.highlight ? 'border-sky-400/50 bg-gradient-to-b from-sky-500/10 to-transparent shadow-[0_0_60px_-15px_rgba(56,189,248,0.4)]' : 'border-slate-800/70 bg-slate-900/40'}`} data-testid={`pricing-${p.tier.toLowerCase()}`}>
                {p.highlight && (
                  <span className="self-start px-2.5 py-0.5 rounded-full bg-sky-400/20 text-sky-300 text-[10px] font-semibold uppercase tracking-widest mb-4">Most popular</span>
                )}
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{p.tier}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">{p.price}</span>
                  {p.period && <span className="text-slate-400 text-sm">{p.period}</span>}
                </div>
                <div className="mt-1 text-sm text-slate-400">{p.best}</div>
                <div className="my-6 h-px bg-slate-800/70" />
                <ul className="space-y-3 text-sm text-slate-300 flex-1">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>{p.scans}</span></li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>{p.users}</span></li>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>
                <Button
                  onClick={() => setDemoOpen(true)}
                  className={`mt-8 ${p.highlight ? 'bg-white text-slate-950 hover:bg-slate-200' : 'bg-slate-800 hover:bg-slate-700 text-white'}`}
                  data-testid={`pricing-cta-${p.tier.toLowerCase()}`}
                >
                  {p.tier === 'Enterprise' ? 'Contact sales' : 'Start free trial'} <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section id="compliance" className="border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Compliance</div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Mapped to BNM AML/CFT and FATF.</h2>
              <p className="mt-6 text-slate-300 leading-relaxed">
                My Shield supports your obligations under BNM&apos;s AML/CFT Policy Document, AMLA 2001, and FATF Recommendations 6, 7, 10, 11, 12, and 18 — with the audit trail to prove it.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {['BNM AML/CFT PD', 'AMLA 2001', 'FATF Rec. 6-12', 'Multi-tenant RBAC', 'Immutable audit logs', 'PDPA-safe storage'].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-sky-400 flex-shrink-0" /> {t}
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-800/70 bg-slate-900/50 p-8 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-indigo-500/10 blur-3xl" />
              <div className="relative">
                <Users2 className="w-8 h-8 text-indigo-300" />
                <blockquote className="mt-5 text-lg text-slate-200 leading-relaxed">
                  &quot;Money changers finally have a tool built for them, not adapted from a global product. The deep-link into MoneyPro was the reason we onboarded in one afternoon.&quot;
                </blockquote>
                <div className="mt-5 text-sm text-slate-400">Compliance officer · Kuala Lumpur licensed MSB</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-slate-800/60">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Ready to see it in action?</h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">
            Book a 20-minute demo. We&apos;ll walk through your existing screening workflow and show you how My Shield fits.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button
              size="lg"
              onClick={() => setDemoOpen(true)}
              className="bg-white text-slate-950 hover:bg-slate-200 font-semibold px-8"
              data-testid="footer-request-demo-btn"
            >
              Request a demo <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-100 px-8">
                I already have an account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>© {new Date().getFullYear()} My Shield Compliance Sdn Bhd</span>
          </div>
          <div className="flex gap-6">
            <a href="mailto:sales@myshield.com.my" className="hover:text-slate-300">sales@myshield.com.my</a>
            <Link to="/login" className="hover:text-slate-300">Log in</Link>
          </div>
        </div>
      </footer>

      {/* DEMO DIALOG */}
      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="sm:max-w-lg bg-slate-950 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-xl">Request a demo</DialogTitle>
            <DialogDescription className="text-slate-400">
              Leave your details and we&apos;ll reach out within one business day.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitDemo} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="demo-name" className="text-slate-300 text-xs">Name *</Label>
                <Input
                  id="demo-name"
                  data-testid="demo-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="bg-slate-900 border-slate-800 mt-1"
                />
              </div>
              <div>
                <Label htmlFor="demo-role" className="text-slate-300 text-xs">Role</Label>
                <Input
                  id="demo-role"
                  data-testid="demo-role-input"
                  placeholder="Compliance officer"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="bg-slate-900 border-slate-800 mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="demo-email" className="text-slate-300 text-xs">Work email *</Label>
              <Input
                id="demo-email"
                data-testid="demo-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="bg-slate-900 border-slate-800 mt-1"
              />
            </div>
            <div>
              <Label htmlFor="demo-company" className="text-slate-300 text-xs">Company *</Label>
              <Input
                id="demo-company"
                data-testid="demo-company-input"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                required
                className="bg-slate-900 border-slate-800 mt-1"
              />
            </div>
            <div>
              <Label htmlFor="demo-message" className="text-slate-300 text-xs">How can we help?</Label>
              <Textarea
                id="demo-message"
                data-testid="demo-message-input"
                rows={3}
                placeholder="Number of branches, current tool, anything else..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-slate-900 border-slate-800 mt-1 resize-none"
              />
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="demo-submit-btn"
                className="bg-white text-slate-950 hover:bg-slate-200 font-semibold w-full sm:w-auto"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <>Send request <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
